/** Intake fields for a company under a law firm—used to tailor follow-up 280G capture. */

import { WORK_STATUS_OPTIONS, isCompanyWorkStatus } from '../../constants/companyWorkStatus';

export {
  COMPANY_WORK_STATUS_VALUES,
  WORK_STATUS_OPTIONS,
  isCompanyWorkStatus,
  type CompanyWorkStatus,
} from '../../constants/companyWorkStatus';

export const DEAL_STRUCTURE_OPTIONS = [
  { value: 'stock_purchase', label: 'Stock purchase' },
  { value: 'asset_purchase', label: 'Asset purchase' },
  { value: 'merger', label: 'Merger / combination' },
  { value: 'other', label: 'Other' },
  { value: 'unsure', label: 'Not sure yet' },
] as const;

export const DEAL_ROLE_OPTIONS = [
  { value: 'target', label: 'Target / seller' },
  { value: 'acquirer', label: 'Acquirer' },
  { value: 'both', label: 'Both' },
  { value: 'other', label: 'Other / unclear' },
  { value: 'unsure', label: 'Not sure yet' },
] as const;

export const LISTING_STATUS_OPTIONS = [
  { value: 'public', label: 'Publicly traded' },
  { value: 'private', label: 'Privately held' },
  { value: 'unsure', label: 'Not sure yet' },
] as const;

function labelFrom(value: string | undefined, options: readonly { value: string; label: string }[]): string {
  const v = value ?? '';
  const hit = options.find((o) => o.value === v);
  if (hit) return hit.label;
  return v ? v : '—';
}

export function dealStructureLabel(value: string | undefined) {
  return labelFrom(value ?? '', DEAL_STRUCTURE_OPTIONS);
}

export function dealRoleLabel(value: string | undefined) {
  return labelFrom(value ?? '', DEAL_ROLE_OPTIONS);
}

export function listingStatusLabel(value: string | undefined) {
  return labelFrom(value ?? '', LISTING_STATUS_OPTIONS);
}

export function workStatusLabel(value: string | undefined) {
  const v = (value ?? '').trim();
  if (!v || !isCompanyWorkStatus(v)) return '—';
  const hit = WORK_STATUS_OPTIONS.find((o) => o.value === v);
  return hit ? hit.label : '—';
}

/** Tailwind classes for compact status chips (list + detail). */
export function workStatusBadgeClasses(value: string | undefined): string {
  const v = (value ?? 'intake').trim() || 'intake';
  if (!isCompanyWorkStatus(v)) {
    return 'bg-slate-50 text-slate-800 border border-slate-200';
  }
  switch (v) {
    case 'in_progress':
      return 'bg-blue-50 text-blue-900 border border-blue-100';
    case 'review':
      return 'bg-violet-50 text-violet-900 border border-violet-100';
    case 'complete':
      return 'bg-emerald-50 text-emerald-900 border border-emerald-100';
    case 'on_hold':
      return 'bg-amber-50 text-amber-900 border border-amber-100';
    case 'intake':
    default:
      return 'bg-slate-50 text-slate-800 border border-slate-200';
  }
}

/** One line for tables: e.g. "Stock purchase · Target / seller" */
export function dealContextSummary(row: { dealStructure?: string; dealRole?: string }): string {
  const d = dealStructureLabel(row.dealStructure);
  const r = dealRoleLabel(row.dealRole);
  if (d === '—' && r === '—') return '';
  if (d === '—') return r;
  if (r === '—') return d;
  return `${d} · ${r}`;
}
